import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { BookCheck, PlugZap, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const when = (d) => (d ? new Date(d).toLocaleString() : "—");

function TestResult({ r }) {
  const Icon = r.ok ? CheckCircle2 : XCircle;
  return (
    <div className={`mt-4 rounded-md border p-3 text-xs ${r.ok ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="ledger-test-result">
      <div className={`flex items-center gap-1.5 font-medium ${r.ok ? "text-primary" : "text-destructive"}`}>
        <Icon className="h-4 w-4" /> <span data-testid="ledger-test-message">{r.message}</span>
      </div>
      {r.accounts && (
        <div className="mt-2 grid gap-1 font-mono text-muted-foreground sm:grid-cols-3">
          {Object.entries(r.accounts).map(([role, id]) => (
            <div key={role} className="truncate" data-testid={`ledger-test-account-${role}`}><span className="uppercase">{role}</span>: {id}</div>
          ))}
        </div>
      )}
      <div className="mt-1 text-muted-foreground">Checked {when(r.checked_at)} · {r.latency_ms} ms</div>
    </div>
  );
}

export function LedgerHealthCard() {
  const [h, setH] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/ledger/health").then((r) => setH(r.data)).catch(() => {}); }, []);

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/ledger/test-connection");
      setResult(r.data);
      r.data.ok ? toast.success("CITIDAN ledger connection OK") : toast.error(r.data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const healthy = h && h.unposted === 0;
  const last = h?.last_retry;
  const stale = last && Date.now() - new Date(last.started_at).getTime() > 2 * 3600 * 1000;
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="ledger-health-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-lg font-medium"><BookCheck className="h-4 w-4 text-primary" /> CITIDAN ledger health</h3>
          <p className="text-xs text-muted-foreground">Counts cover {h?.scope || "…"}.</p>
        </div>
        <Button variant="outline" size="sm" onClick={test} disabled={testing} data-testid="ledger-test-connection-btn">
          <PlugZap className={`mr-1.5 h-4 w-4 ${testing ? "animate-pulse" : ""}`} /> {testing ? "Testing…" : "Test connection"}
        </Button>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-8">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Unposted entries</div>
          <div className="flex items-center gap-2">
            <span className={`font-mono text-3xl font-semibold ${healthy ? "text-primary" : "text-destructive"}`} data-testid="ledger-unposted-count">{h?.unposted ?? "—"}</span>
            {h && (
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${healthy ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`} data-testid="ledger-health-badge">
                {healthy ? "All posted" : `${h.failed} failed · ${h.pending} pending`}
              </span>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground" data-testid="ledger-last-retry">
          <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider"><Clock className="h-3 w-3" /> Last hourly retry</div>
          <div className={`mt-1 text-sm ${stale ? "text-amber-500" : "text-foreground"}`} data-testid="ledger-last-retry-time">{last ? when(last.started_at) : "Not run yet"}</div>
          {stale && <div className="text-amber-500" data-testid="ledger-retry-stale">Over 2 hours ago — hourly retry may not be running</div>}
          {last && last.attempted != null && <div>{last.posted}/{last.attempted} re-sent successfully</div>}
        </div>
        <div className="text-xs text-muted-foreground">
          <div className="text-[10px] font-mono uppercase tracking-wider">Posted</div>
          <div className="mt-1 font-mono text-sm text-foreground" data-testid="ledger-posted-count">{h?.posted ?? "—"}</div>
        </div>
      </div>
      {result && <TestResult r={result} />}
    </div>
  );
}
